import { ArrowLeftIcon, CheckIcon, DocumentDuplicateIcon } from '@heroicons/react/24/solid';
import { Button } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import type { RootState } from '../../../store/store';
import { isLightColor } from '../../../utils/newsletterPreview';
import { ROUTES } from '../../../utils/routes';
import { showToast } from '../../common/Notification';

const PageSite = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [copied, setCopied] = useState(false);
  const [currentDomain, setCurrentDomain] = useState('');
  const company = useSelector((state: RootState) => state.auth.company);
  const primaryColor = company?.brandingSettings?.color1 || '#1a237e';
  const textColor = isLightColor(primaryColor) ? '#000000' : '#ffffff';

  const handleBack = () => {
    if (location.key !== 'default') {
      navigate(-1);
    } else {
      navigate(ROUTES.COMMUNICATION_NEWSLETTER_LIST);
    }
  };

  useEffect(() => {
    // Get the current domain from the browser
    setCurrentDomain(window.location.origin);
  }, []);

  const htmlSnippet = `<a
  href="${currentDomain}/blog/company/${company?.siren}"
  target="_blank"
  style="
    display: inline-flex;
    align-items: center;
    padding: 0.75rem 1.5rem;
    background-color: ${primaryColor};
    color: ${textColor};
    font-family: system-ui, -apple-system, sans-serif;
    font-weight: 500;
    text-decoration: none;
    border-radius: 0.5rem;
    transition: opacity 0.2s ease-in-out;
  "
  onmouseover="this.style.opacity='0.9'"
  onmouseout="this.style.opacity='1'"
>
  Accéder à notre blog COPRAVIE
</a>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(htmlSnippet)
      .then(() => {
        setCopied(true);
        showToast('success', 'Code copié avec succès !');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        showToast('error', 'Erreur lors de la copie du code.');
      });
  };

  return (
    <div className="rounded-lg bg-white p-8 shadow-md">
      <div className="mb-8 flex items-center">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Retour</span>
        </button>
      </div>

      <h2 className="mb-6 text-2xl font-bold text-gray-800">Page à inclure sur votre site</h2>
      
      <div className="mb-8">
        <p className="mb-4 text-gray-600">
          Pour intégrer le bouton d'accès à votre blog COPRAVIE sur votre site web, suivez ces étapes simples :
        </p>
        <ol className="mb-6 list-inside list-decimal space-y-2 text-gray-600">
          <li>Copiez le code HTML ci-dessous en cliquant sur le bouton "Copier le code"</li>
          <li>Collez ce code à l'endroit souhaité dans le code source de votre site web</li>
          <li>Le bouton apparaîtra automatiquement avec le style approprié</li>
        </ol>
      </div>

      <div className="mb-8">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-700">Code HTML à copier</h3>
          <Button
            onClick={handleCopy}
            className="bg-rgpd-blue text-white transition-colors hover:bg-rgpd-blue-dark"
          >
            {copied ? (
              <CheckIcon className="mr-2 h-5 w-5" />
            ) : (
              <DocumentDuplicateIcon className="mr-2 h-5 w-5" />
            )}
            {copied ? 'Copié !' : 'Copier le code'}
          </Button>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <pre className="font-mono text-sm whitespace-pre-wrap text-gray-800">
            {htmlSnippet}
          </pre>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="mb-4 text-lg font-semibold text-gray-700">Aperçu du rendu</h3>
        <div className="rounded-lg border border-gray-200 p-4">
          <div dangerouslySetInnerHTML={{ __html: htmlSnippet }} />
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <h4 className="mb-2 font-semibold text-blue-800">Note importante</h4>
        <p className="text-blue-600">
          Le bouton utilise votre couleur principale définie dans vos paramètres de personnalisation. 
          La couleur du texte s'adapte automatiquement pour garantir une lisibilité optimale.
          Le style est intégré directement dans le bouton pour une compatibilité maximale avec tous les sites web.
        </p>
      </div>
    </div>
  );
};

export default PageSite; 